import React, { useEffect, useState, useCallback } from 'react';
import { Input, Form, Skeleton, Drawer, DatePicker, notification, Checkbox, Alert, Popconfirm, Switch, } from 'antd';
import FormDrawer from '../common/formdrawer';
import PageVersionStatus from './pageversionstatus';
import StoreSelector from './storeselector';
import FormButton from '../common/formbutton';
import CancelButton from '../common/cancelbutton';
import { SelectOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { getAllStores } from '../../services/storeserivce';
import { getPublishDefaults, validatePublishOverlap, getScheduledAssignments, } from '../../services/pageversionservice';
import { PageVersionConflict } from '../../types/pageversiontypes';
import { StoresListToPublish } from '../../types/storetypes';
import { ENABLE_OVERRIDE } from '../../config/appconfig';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface PublishPageVersionFormProps {
  open: boolean;
  onClose: () => void;
  onPublish: (data: any) => void;
  pageVersionName: string;
  pageVersionGuid: string;
}

const PublishPageVersionForm: React.FC<PublishPageVersionFormProps> = ({
  open,
  onClose,
  onPublish,
  pageVersionName,
  pageVersionGuid,
}) => {
  const [form] = Form.useForm();
  const initialStatus = 'draft';
  const [allStores, setAllStores] = useState<StoresListToPublish[]>([]);
  const [childrenDrawer, setChildrenDrawer] = useState(false);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [overlapErrors, setOverlapErrors] = useState<Record<string, string>>({});
  const [isPublishing, setIsPublishing] = useState(false);
  const [scheduledAssignments, setScheduledAssignments] = useState<Record<string, string>>({});
  const [noEndDate, setNoEndDate] = useState(false);
  const [overrideEnabled, setOverrideEnabled] = useState(false);

  const fetchStores = useCallback(async () => {
    try {
      const stores = await getAllStores();
      setAllStores(stores);
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPublishDefaults = async () => {
    try {
      const data = await getPublishDefaults(pageVersionGuid);
      form.setFieldsValue({
        name: data.name,
        startDate: data.startDate ? dayjs(data.startDate) : null,
        endDate: data.endDate ? dayjs(data.endDate) : null,
        stores: data.storeSelections || [],
      });
      if (data.storeSelections) {
        setSelectedStores(data.storeSelections);
      }
    } catch (error) {
      console.error('Error fetching publish defaults:', error);
    }
  };

  useEffect(() => {
    if (open && pageVersionName) {
      setLoading(true);
      fetchStores();
      fetchPublishDefaults();
      setOverlapErrors({});
      setIsPublishing(false);
    }
  }, [open, pageVersionName, form, fetchStores]);

  const handleNoEndDateChange = (e: any) => {
    const checked = e.target.checked;
    setNoEndDate(checked);

    if (checked) {
      form.setFieldsValue({
        endDate: dayjs('9999-12-31 23:59:59'),
      });
    } else {
      form.setFieldsValue({
        endDate: null,
      });
    }
  };

  const checkPublishOverlap = async (
    storeGuids: string[],
    startDate: Dayjs,
    endDate: Dayjs | null
  ): Promise<{ isValid: boolean; conflicts: PageVersionConflict[] }> => {
    setOverlapErrors({});
    try {
      const response = await validatePublishOverlap(
        storeGuids,
        startDate.format('YYYY-MM-DD HH:mm:ss'),
        endDate ? endDate.format('YYYY-MM-DD HH:mm:ss') : null,
        pageVersionName,
        pageVersionGuid
      );

      const conflicts: PageVersionConflict[] = response.conflicts || [];
      const newOverlapErrors: Record<string, string> = {};
      let hasConflicts = false;

      conflicts.forEach((conflict) => {
        const overlappingVersions = conflict.overlappingPageVersions
          .map((pv) => {
            const typeLabel =
              pv.conflictType === 'Pending' ? 'Pending Approval' : 'Finalized';

            return `• "${pv.pageVersionName}" [${typeLabel}] (${dayjs(pv.startDate).format(
              'YYYY-MM-DD HH:mm'
            )} → ${dayjs(pv.endDate).format('YYYY-MM-DD HH:mm')})`;
          })
          .join('\n');

        newOverlapErrors[conflict.storeGuid] =
          `Store ${conflict.storeNumber} (${conflict.buildingName}):\n${overlappingVersions}`;

        hasConflicts = true;
      });

      setOverlapErrors(newOverlapErrors);

      if (hasConflicts && !(ENABLE_OVERRIDE && overrideEnabled)) {
        notification.warning({
          message: 'Publishing Conflicts Detected',
          description:
            'Some selected stores have overlapping page versions. Please review the errors below and adjust dates or stores.',
          duration: 5,
        });
        return { isValid: false, conflicts };
      }

      return { isValid: true, conflicts };
    } catch (error) {
      console.error('Error during overlap validation:', error);
      setOverlapErrors({ general: 'Failed to validate store overlaps. Please try again.' });
      notification.error({
        message: 'Validation Error',
        description: 'Could not perform overlap validation. Please check your network or try again.',
      });
      return { isValid: false, conflicts: [] };
    }
  };

  const handleStoreSelect = useCallback(
    (storeGuids: string[]) => {
      setSelectedStores(storeGuids);
      form.setFieldsValue({ stores: storeGuids });
      setChildrenDrawer(false);
    },
    [form]
  );

  const handlePublishClick = async (values: any) => {
    setIsPublishing(true);
    try {

      const result = await checkPublishOverlap(
        selectedStores,
        values.startDate,
        values.endDate
      );

      if (!result.isValid && !(ENABLE_OVERRIDE && overrideEnabled)) {
        if (ENABLE_OVERRIDE) {
          notification.warning({
            message: 'Override Required',
            description: 'Conflicts exist. Please enable override to proceed.',
          });
        }
        return;
      }

      const publishData = {
        ...values,
        stores: selectedStores,
        startDate: values.startDate?.format('YYYY-MM-DD HH:mm:ss') || null,
        endDate: values.endDate?.format('YYYY-MM-DD HH:mm:ss') || null,
        override: ENABLE_OVERRIDE ? overrideEnabled : false,
        conflicts: result.conflicts
      };

      onPublish(publishData);

      //Reset form + state
      setSelectedStores([]);
      form.resetFields();
      setOverlapErrors({});
      setOverrideEnabled(false);

    } finally {
      setIsPublishing(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
    setSelectedStores([]);
    setOverlapErrors({});
    setIsPublishing(false);
    setNoEndDate(false);

    setOverrideEnabled(false);
  };

  const footer = (
    <div style={{ textAlign: 'right' }}>
      <CancelButton onClick={handleClose} />
      <FormButton
        title="Request Approval"
        onClick={() => {
          setOverlapErrors({});
          form
            .validateFields()
            .then(async (values) => {
              await handlePublishClick(values);
            })
            .catch((err) => {
              console.log('Validation failed:', err);
            });
        }}
        loading={isPublishing}
      />
    </div>
  );

  const showChildrenDrawer = async () => {
    try {
      const { startDate, endDate } = form.getFieldsValue();
      if (!startDate || !endDate) {
        notification.warning({
          message: 'Missing Dates',
          description: 'Please select both start and end dates before viewing scheduled assignments.',
        });
        return;
      }

      const response = await getScheduledAssignments(
        startDate.format('YYYY-MM-DD HH:mm:ss'),
        endDate.format('YYYY-MM-DD HH:mm:ss')
      );

      const assignmentMap: Record<string, string> = {};
      response.forEach((a: any) => {
        assignmentMap[a.storeGuid] = a.pageVersionName;
      });

      setScheduledAssignments(assignmentMap);
    } catch (error) {
      console.error('Error fetching scheduled assignments:', error);
      notification.error({
        message: 'Assignment Fetch Error',
        description: 'Could not retrieve scheduled assignments. Please try again.',
      });
    }

    setChildrenDrawer(true);
  };

  const disabledDate = (current: Dayjs) => {
    return current && current.isBefore(dayjs().startOf('day'));
  };

  const validateStores = (_: any, value: any) => {
    if (selectedStores.length === 0) {
      return Promise.reject(new Error('Please select at least one store.'));
    }
    if (Object.keys(overlapErrors).length > 0) {
      return Promise.reject(
        new Error('Conflicts detected with selected stores. Please review the messages above.')
      );
    }
    return Promise.resolve();
  };

  return (
    <FormDrawer
      title="Publish PageVersion"
      open={open}
      width={600}
      onClose={handleClose}
      onSave={() => { }}
      form={form}
      footer={footer}
    >
      {loading ? (
        <Skeleton active />
      ) : (
        <>
          <PageVersionStatus status={initialStatus} />

          <Form.Item name="name" label="Name">
            <Input disabled placeholder="Enter pageversion name" />
          </Form.Item>

          <Form.Item
            label="Start Date"
            name="startDate"
            style={{ marginTop: '16px' }}
            dependencies={['endDate']}
            rules={[
              { required: true, message: 'Please select start date' },
              {
                validator: (_, value, callback) => {
                  const endDate = form.getFieldValue('endDate');
                  if (endDate && value && value.isAfter(endDate)) {
                    callback('Start date must be before end date');
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            <DatePicker
              showTime={{ format: 'hh:mm A', use12Hours: true }}
              format="YYYY-MM-DD hh:mm A"
              style={{ width: '100%' }}
              disabledDate={disabledDate}
            />
          </Form.Item>

          <Form.Item
            label="End Date"
            name="endDate"
            dependencies={['startDate']}
            rules={[
              {
                validator: (_, value, callback) => {
                  const startDate = form.getFieldValue('startDate');
                  if (startDate && value && value.isBefore(startDate)) {
                    callback('End date must be after start date');
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            <DatePicker
              showTime={{ format: 'hh:mm A', use12Hours: true }}
              format="YYYY-MM-DD hh:mm A"
              style={{ width: '100%' }}
              disabledDate={disabledDate}
              disabled={noEndDate}
            />
          </Form.Item>

          <Form.Item>
            <Checkbox checked={noEndDate} onChange={handleNoEndDateChange}>
              No End Date
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="stores"
            label="Stores"
            required
            rules={[{ validator: validateStores }]}
            validateStatus={Object.keys(overlapErrors).length > 0 ? 'error' : undefined}
            help={
              Object.keys(overlapErrors).length > 0 ? (
                <div style={{ color: '#ff4d4f' }}>
                  <p>Storewise Overlapping Pageversions</p>
                  {Object.values(overlapErrors).map((msg, index) => (
                    <div key={index} style={{ whiteSpace: 'pre-line' }}>
                      {msg}
                    </div>
                  ))}
                </div>
              ) : null
            }
          >
            <div>
              {selectedStores.length > 0 ? (
                <div style={{ marginBottom: '8px', color: 'rgba(0, 0, 0, 0.45)' }}>
                  {`Selected: ${selectedStores.length} store(s)`}
                </div>
              ) : (
                <div style={{ marginBottom: '8px', color: 'rgba(0, 0, 0, 0.45)' }}>
                  No stores selected
                </div>
              )}
              <FormButton
                title="Select Stores"
                icon={<SelectOutlined />}
                onClick={showChildrenDrawer}
              />
            </div>
          </Form.Item>
          {Object.keys(overlapErrors).length > 0 && ENABLE_OVERRIDE && (
            <>
              <Form.Item label="Override Overlapping PageVersions">
                <Popconfirm
                  title="Enable Override"
                  description={`This will add pageversion with new schedule along with existing scheduled pageversions for ${selectedStores.length} store(s). Are you sure?`}
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => setOverrideEnabled(true)}
                  onCancel={() => setOverrideEnabled(false)}
                >
                  <Switch
                    checked={overrideEnabled}
                    size='small'
                    onChange={(checked) => {
                      if (!overrideEnabled && checked) {
                        return;
                      }
                      setOverrideEnabled(false);
                    }}
                  />
                </Popconfirm>
              </Form.Item>

              {overrideEnabled && (
                <Alert
                  type="warning"
                  message="Override Enabled"
                  description="This will add pageversion with new schedule along with existing scheduled pageversions for selected stores"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
            </>
          )}

          <Drawer
            title="Select Stores"
            placement="right"
            width={580}
            onClose={() => setChildrenDrawer(false)}
            open={childrenDrawer}
            destroyOnClose
            styles={{
              header: {
                color: 'white',
                backgroundColor: 'var(--color-primary)',
                fontWeight: 350,
                fontSize: '14px',
                border: 0,
                minHeight: '40px',
              },
            }}
          >
            <StoreSelector
              stores={allStores}
              selectedStores={selectedStores}
              onStoreSelect={handleStoreSelect}
              scheduledAssignments={scheduledAssignments}
            />
          </Drawer>
        </>
      )}
    </FormDrawer>
  );
}
export default PublishPageVersionForm;